import omit from "lodash/omit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { DataSourceHandler } from "../../../api/data-source-handler";
import Form, { InputField, NewSelectField, RadioField } from "../../../components/form";
import { Modal } from "../../../components/modals";
import CustomMonacoEditor from "../../../components/monacoEditor";
import { errorAlert } from "../../../components/toastify/notify-toast";
import { toggleDataBrowserModal } from "../../../store/datasources";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { PiRequestInfo } from "../pi-managed-requests-table";
import { TableMetaData } from "../treeviews/connections-treeview";
import { _selectoptionType } from '../../../components/form/select-field';
import * as yup from "yup";
import styles from "../styles.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "@store/types";
import { Flex } from "@components/ui/Flex";
import { Text } from "@components/ui/Text";
import { GoToFullScreenIcon, RestoreIcon } from "@pages/workflow-analytics-page/assets/icons";
import { CloseTabIcon, InformativeIcon } from "@pages/workflow-page/assets/icons";
import { ToggleFieldFormik } from "../../../components/form/toggle-field-formik";

export type NewEditPiRequestModalTypes = "new" | "edit" | "view";

type Props = {
	activeRecordsMetadata: TableMetaData;
	refreshActivePrRecords: () => void;
	type: NewEditPiRequestModalTypes;
	selectedPiRequestInfo: PiRequestInfo | null;
	modelRequestId:number|null,
};

const _modalTitles: Record<NewEditPiRequestModalTypes, string> = {
	new: "Create",
	view: "View",
	edit: "Edit",
};

const workflowConfigTypeOptions = [
	{ label: "String", value: "string" },
	{ label: "Boolean", value: "boolean" },
	{ label: "Decimal", value: "decimal" },
	{ label: "Integer", value: "integer" },
];

const booleanValueOptions = [
	{ label: "True", value: "true" },
	{ label: "False", value: "false" },
];

const workflowConfigItemSchema = yup.object().shape({
	key: yup.string().trim().required("Key is required"),
	type: yup.string().oneOf(workflowConfigTypeOptions.map(({ value }) => value)).required("Type is required"),
	value: yup
		.string()
		.required("Value is required")
		.test("matches-config-type", "Value does not match the selected type", function (value) {
			if (!value) return true;
			switch (this.parent.type) {
				case "boolean":
					return /^(true|false)$/i.test(value);
				case "integer":
					return /^-?\d+$/.test(value);
				case "decimal":
					return /^-?(\d+\.\d+|\d+)$/.test(value);
				default:
					return true;
			}
		}),
});

const dagConfigItemSchema = yup.object().shape({
	key: yup.string().trim().required("Key is required"),
	value: yup.string().required("Value is required"),
});

const formSchema = yup.object().shape({
	requestName: yup
		.string()
		.required("This is a required field")
		.matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
	sinkName: yup
		.string()
		.required("This is a required field"),
	successWorkflowConfig: yup.array().of(workflowConfigItemSchema),
	failureWorkflowConfig: yup.array().of(workflowConfigItemSchema),
	successDagConfig: yup.array().of(dagConfigItemSchema),
	failureDagConfig: yup.array().of(dagConfigItemSchema),
	enableNotificationOnFailure: yup.boolean(),
	notificationEmails: yup.string(),
	notificationTriggerType: yup.string().oneOf(["success", "failure", "both"]),
});

const OpcuaFormSchema = yup.object().shape({
	requestName: yup
		.string()
		.required("This is a required field")
		.matches(/^[A-Za-z0-9_-]+$/, "Special Characters are not allowed except _ and -"),
	sinkName: yup
		.string()
		.required("This is a required field"),
	successWorkflowConfig: yup.array().of(workflowConfigItemSchema),
	failureWorkflowConfig: yup.array().of(workflowConfigItemSchema),
	successDagConfig: yup.array().of(dagConfigItemSchema),
	failureDagConfig: yup.array().of(dagConfigItemSchema),
	enableNotificationOnFailure: yup.boolean(),
	notificationEmails: yup.string(),
	notificationTriggerType: yup.string().oneOf(["success", "failure", "both"]),
});

type TriggerType = "workflow" | "dag" | "";
type ConfigPair = { key: string; value: string; type?: string };
type PiRequestFormValues = {
	requestName: string;
	sinkName: string;
	successTriggerType: TriggerType;
	failureTriggerType: TriggerType;
	successWorkflowId: string;
	failureWorkflowId: string;
	successDagName: string;
	failureDagName: string;
	successWorkflowConfig: ConfigPair[];
	failureWorkflowConfig: ConfigPair[];
	successDagConfig: ConfigPair[];
	failureDagConfig: ConfigPair[];
	enableNotificationOnFailure: boolean;
	notificationEmails: string;
	notificationTriggerType: NotificationTriggerType;
};

const triggerTypeOptions = [
	{ label: "Workflows", value: "workflow" },
	{ label: "DAGs", value: "dag" },
];

type NotificationTriggerType = "success" | "failure" | "both";

const notificationTriggerOptions: { label: string; value: NotificationTriggerType }[] = [
	{ label: "On Success", value: "success" },
	{ label: "On Failure", value: "failure" },
	{ label: "Both", value: "both" },
];

/*
 * Derives the saved trigger scope (success/failure/both) from the two independent backend flags
 */
const deriveNotificationTriggerType = (info: PiRequestInfo): NotificationTriggerType => {
	if (info.enableNotificationOnSuccess && info.enableNotificationOnFailure) return "both";
	if (info.enableNotificationOnSuccess) return "success";
	return "failure";
};

const TriggerConfigurationFields: React.FC<{
	trigger: "success" | "failure";
	workflowOptions: _selectoptionType[];
	dagOptions: _selectoptionType[];
}> = ({ trigger, workflowOptions, dagOptions }) => {
	const { values, setFieldValue } = useFormikContext<PiRequestFormValues>();
	const triggerType = trigger === "success" ? values.successTriggerType : values.failureTriggerType;
	const isWorkflow = triggerType === "workflow";
	const selectionName = isWorkflow ? `${trigger}WorkflowId` : `${trigger}DagName`;
	const configName = isWorkflow ? `${trigger}WorkflowConfig` : `${trigger}DagConfig`;
	const configPairs = isWorkflow
		? (trigger === "success" ? values.successWorkflowConfig : values.failureWorkflowConfig)
		: (trigger === "success" ? values.successDagConfig : values.failureDagConfig);
	const selectedValue = isWorkflow
		? (trigger === "success" ? values.successWorkflowId : values.failureWorkflowId)
		: (trigger === "success" ? values.successDagName : values.failureDagName);
	const title = trigger === "success" ? "Success" : "Failure";

	const changeTriggerType = (nextType: TriggerType) => {
		setFieldValue(`${trigger}TriggerType`, nextType);
		setFieldValue(`${trigger}WorkflowId`, "");
		setFieldValue(`${trigger}DagName`, "");
		setFieldValue(`${trigger}WorkflowConfig`, []);
		setFieldValue(`${trigger}DagConfig`, []);
	};

	return (
		<div className={styles.triggerConfiguration}>
			<RadioField
				name={`${trigger}TriggerType`}
				label={`${title} Trigger`}
				options={triggerTypeOptions}
				inline
				onChange={changeTriggerType}
				className={styles.triggerTypeRadio}
			/>
			{triggerType && (
				<>
					{(isWorkflow ? workflowOptions : dagOptions).length ? (
						<NewSelectField
							name={selectionName}
							label={`${title} ${isWorkflow ? "Workflow" : "DAG"}`}
							options={isWorkflow ? workflowOptions : dagOptions}
							className={styles.requestName__field}
						/>
					) : (
						<div className={styles.triggerEmptyState}>
							No {isWorkflow ? "workflows" : "DAGs"} available.
						</div>
					)}
					{selectedValue && (
						<div className={styles.triggerConfigValues}>
							<div className={styles.triggerConfigHeader}>
								<span style={{ color: "white",fontSize:12 }}>{isWorkflow ? "Workflow Config" : "DAG Config"}</span>
								<button
									type="button"
									className="btn-sm btn-yellow"
									onClick={() => setFieldValue(configName, [...configPairs, isWorkflow ? { key: "", value: "", type: "string" } : { key: "", value: "" }])}
								>
									Add Config
								</button>
							</div>
							{configPairs.map((configPair, index) => (
								<div className={`${styles.triggerConfigRow} ${isWorkflow ? styles.workflowTriggerConfigRow : ""}`} key={`${configName}-${index}`}>
									<InputField name={`${configName}.${index}.key`} label="Key" required />
									{isWorkflow && (
										<NewSelectField
											name={`${configName}.${index}.type`}
											label="Type"
											options={workflowConfigTypeOptions}
											required
											onOptionClick={(option) => {
												if (option.value === "boolean" && !["true", "false"].includes(configPair.value)) {
													setFieldValue(`${configName}.${index}.value`, "true");
												}
											}}
										/>
									)}
									{isWorkflow && configPair.type === "boolean" ? (
										<NewSelectField
											name={`${configName}.${index}.value`}
											label="Value"
											options={booleanValueOptions}
											required
										/>
									) : (
										<InputField
											name={`${configName}.${index}.value`}
											label="Value"
											type={isWorkflow && (configPair.type === "integer" || configPair.type === "decimal") ? "number" : "text"}
											required
										/>
									)}
									<button
										type="button"
										style={{ cursor: 'pointer' }}
										onClick={() => setFieldValue(configName, configPairs.filter((_, pairIndex) => pairIndex !== index))}
									>
										
										<CloseTabIcon/>
									</button>
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
};

const NotificationConfigurationFields: React.FC<{
	userOptions: _selectoptionType[];
	currentUserEmail?: string;
}> = ({ userOptions, currentUserEmail }) => {
	const { values } = useFormikContext<PiRequestFormValues>();

	return (
		<div className={styles.triggerConfiguration}>
			<ToggleFieldFormik name="enableNotificationOnFailure" label="Email Alert" />
			{values.enableNotificationOnFailure && (
				<>
					<RadioField
						name="notificationTriggerType"
						label="Trigger On"
						options={notificationTriggerOptions}
						inline
						className={styles.triggerTypeRadio}
					/>
					<NewSelectField
						name="notificationEmails"
						label="Select Users"
						placeholder="Select users"
						options={userOptions}
						multiple_select
						className={styles.requestName__field}
					/>
					<div className={styles.notificationInfoBanner}>
						<InformativeIcon />
						<Text fontSize={12} fontWeight={500}>
							If no users are selected, notifications will be sent to your email ({currentUserEmail}) by default.
						</Text>
					</div>
				</>
			)}
		</div>
	);
};

const NewEditPiRequestModal: React.FC<Props> = ({
	activeRecordsMetadata,
	refreshActivePrRecords,
	selectedPiRequestInfo,
	modelRequestId,
	type
}) => {
	const showModal = useAppSelector(
		(store) => store.NewDataSourceReducer.showModal.newPiRequest
	);
	const [isFullScreen, setIsFullScreen] = useState(false);
	const editorControllerProps = CustomMonacoEditor.useCustomMonacoEditorController();
	const dispatch = useAppDispatch();
	const [sinkOptions, setSinkOptions] = useState<{label:string,value:string,type:string}[]>([]);
	const [workflowOptions,setWorkflows]= useState<_selectoptionType[]>([]);
	const [dagOptions, setDagOptions] = useState<_selectoptionType[]>([]);
	const { envVariables: Env, allUsersList, activeUserInfo } = useSelector((store:RootState)=> store.AccountReducer);

	/*
	 * Options for the notification recipients dropdown, sourced from the workspace's user list
	 */
	const notificationUserOptions = useMemo(
		() => allUsersList.map((user) => ({
			label: `${user.firstName} ${user.lastName ? user.lastName.charAt(0) : ''} (${user.email})`,
			value: user.email
		})),
		[allUsersList]
	);
	useEffect(() => {
		if (showModal) {
			if (selectedPiRequestInfo) {
				try {
					let requestJson = JSON.parse(selectedPiRequestInfo.json);
					requestJson = omit(requestJson, "request_id");
					editorControllerProps.setEditorValue(
						JSON.stringify(requestJson, null, 4)
					);
				} catch {
					errorAlert("Error parsing the Request body");
				}
			} else {
				editorControllerProps.setEditorValue("");
			}
		}
	}, [showModal, type, selectedPiRequestInfo]);

	const toggleClose = () => {
		editorControllerProps.clearEditor();
		dispatch(toggleDataBrowserModal("newPiRequest", false));
	};

	const onRequestSuccess = () => {
		refreshActivePrRecords();
		toggleClose();
	};

	const isValidJson = (str: string): boolean => {
		try {
			JSON.parse(str);
			return true;
		} catch (e) {
			return false;
		}
	};

	const handleOnSubmit = (values: any) => {
		const editorValue = editorControllerProps.editorValue;

		if (!isValidJson(editorValue)) {
			errorAlert("Invalid JSON");
			return;
		}
		try {
			const selectedSink = sinkOptions.find((item) => item.label === values.sinkName);
			const sinkKind = selectedSink ? selectedSink.type : ''; 
			const parsedJson = JSON.parse(editorValue); 
			const toConfig = (configPairs: ConfigPair[], isWorkflow: boolean) => configPairs.reduce((config, { key, value, type }) => {
				if (key.trim()) {
					const typedValue = type === "boolean"
						? value === "true"
						: type === "integer" || type === "decimal"
							? Number(value)
							: value;
					config[key.trim()] = isWorkflow ? { key: key.trim(), value: typedValue, type } : value;
				}
				return config;
			}, {} as Record<string, any>);
			const triggers = {
				...(values.successTriggerType === "workflow" && values.successWorkflowId ? {
					successWorkflow: {
						workflowId: values.successWorkflowId,
						workflow_config: toConfig(values.successWorkflowConfig, true),
					},
				} : {}),
				...(values.failureTriggerType === "workflow" && values.failureWorkflowId ? {
					failureWorkflow: {
						workflowId: values.failureWorkflowId,
						workflow_config: toConfig(values.failureWorkflowConfig, true),
					},
				} : {}),
				...(values.successTriggerType === "dag" && values.successDagName ? {
					successDag: {
						dagName: values.successDagName,
						dagConf: toConfig(values.successDagConfig, false),
					},
				} : {}),
				...(values.failureTriggerType === "dag" && values.failureDagName ? {
					failureDag: {
						dagName: values.failureDagName,
						dagConf: toConfig(values.failureDagConfig, false),
					},
				} : {}),
			};
			const notificationEmailsList = values.notificationEmails
				? values.notificationEmails.split(",").map((email: string) => email.trim()).filter(Boolean)
				: [];
			const enableNotificationOnFailure = values.enableNotificationOnFailure
				&& values.notificationTriggerType !== "success";
			const enableNotificationOnSuccess = values.enableNotificationOnFailure
				&& values.notificationTriggerType !== "failure";
			if (type === "edit" && selectedPiRequestInfo) {
				parsedJson.request_id = selectedPiRequestInfo.requestId;
				DataSourceHandler.UpdatePiRequestJson(
					`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
					selectedPiRequestInfo.requestId,
					values.requestName,
					JSON.stringify(parsedJson),
					values.sinkName,
					sinkKind,
					triggers,
					onRequestSuccess,
					enableNotificationOnFailure,
					notificationEmailsList,
					enableNotificationOnSuccess
				);
			} else if(activeRecordsMetadata.managedNodeInfo?.nodeName){
				DataSourceHandler.CreatePiNodeRequest(
					`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`,
					activeRecordsMetadata.managedNodeInfo.nodeName,
					values.requestName,
					editorControllerProps.editorValue,
					values.sinkName || '',
					sinkKind||'',
					modelRequestId,
					activeRecordsMetadata?.sourceType,
				    activeRecordsMetadata.connectionName,
					triggers,
					onRequestSuccess,
					enableNotificationOnFailure,
					notificationEmailsList,
					enableNotificationOnSuccess
				);
			}else if(activeRecordsMetadata.nodeName){
				DataSourceHandler.CreatePiNodeRequest(
					`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, 
					activeRecordsMetadata.nodeName,
					values.requestName,
					editorControllerProps.editorValue,
					values.sinkName,
					sinkKind,
					modelRequestId,
					activeRecordsMetadata?.sourceType,
				    activeRecordsMetadata.connectionName,
					triggers,
					onRequestSuccess,
					enableNotificationOnFailure,
					notificationEmailsList,
					enableNotificationOnSuccess
				);
			}
		} catch (e) {
			errorAlert("Invalid JSON");
		}
	};

	const [initialValues, setIntitialValues] = useState<PiRequestFormValues>({
		requestName: "",
		sinkName: "",
		successTriggerType: "workflow",
		failureTriggerType: "workflow",
		successWorkflowId: "",
		failureWorkflowId: "",
		successDagName: "",
		failureDagName: "",
		successWorkflowConfig: [],
		failureWorkflowConfig: [],
		successDagConfig: [],
		failureDagConfig: [],
		enableNotificationOnFailure: false,
		notificationEmails: "",
		notificationTriggerType: "failure",
	});

	useEffect(() => {
		if(showModal && activeRecordsMetadata.managedNodeInfo?.nodeName) {
			if (type === "new") { 
				// sending state as empty returns all records 
				DataSourceHandler.GetRequestsOfPiNode(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata.managedNodeInfo.nodeName, { page: 1, size: 5, filter: "", sort: "stateUpdatedDate,desc", state: [] }, (res) => {
					// sinkName should be  in response -- will add it once done from backend
					setIntitialValues({
						requestName: "request_" + res.totalItems,
						sinkName: "",
						successTriggerType: "workflow",
						failureTriggerType: "workflow",
						successWorkflowId: "",
						failureWorkflowId: "",
						successDagName: "",
						failureDagName: "",
						successWorkflowConfig: [],
						failureWorkflowConfig: [],
						successDagConfig: [],
						failureDagConfig: [],
						enableNotificationOnFailure: false,
						notificationEmails: "",
						notificationTriggerType: "failure",
					})
				})
			} else if (type === "edit" && selectedPiRequestInfo?.requestName  && selectedPiRequestInfo?.sinkName) {
				// sinkName should be  in response -- will add it once done from backend
				setIntitialValues({
					requestName: selectedPiRequestInfo.requestName,
					sinkName: selectedPiRequestInfo.sinkName,
					successTriggerType: selectedPiRequestInfo.successWorkflowId ? "workflow" : "workflow",
					failureTriggerType: selectedPiRequestInfo.failureWorkflowId ? "workflow" : "workflow",
					successWorkflowId: selectedPiRequestInfo.successWorkflowId || "",
					failureWorkflowId: selectedPiRequestInfo.failureWorkflowId || "",
					successDagName: "",
					failureDagName: "",
					successWorkflowConfig: [],
					failureWorkflowConfig: [],
					successDagConfig: [],
					failureDagConfig: [],
					enableNotificationOnFailure: !!(selectedPiRequestInfo.enableNotificationOnFailure || selectedPiRequestInfo.enableNotificationOnSuccess),
					notificationEmails: (selectedPiRequestInfo.notificationEmails || []).join(","),
					notificationTriggerType: deriveNotificationTriggerType(selectedPiRequestInfo),
				})
			}  else if (type === "edit" && selectedPiRequestInfo?.requestName && activeRecordsMetadata.sourceType=='OPCUA') {
				// sinkName should be  in response -- will add it once done from backend
				setIntitialValues({
					requestName: selectedPiRequestInfo.requestName,
					sinkName: selectedPiRequestInfo.sinkName || "",
					successTriggerType: selectedPiRequestInfo.successWorkflowId ? "workflow" : "workflow",
					failureTriggerType: selectedPiRequestInfo.failureWorkflowId ? "workflow" : "workflow",
					successWorkflowId: selectedPiRequestInfo.successWorkflowId || "",
					failureWorkflowId: selectedPiRequestInfo.failureWorkflowId || "",
					successDagName: "",
					failureDagName: "",
					successWorkflowConfig: [],
					failureWorkflowConfig: [],
					successDagConfig: [],
					failureDagConfig: [],
					enableNotificationOnFailure: !!(selectedPiRequestInfo.enableNotificationOnFailure || selectedPiRequestInfo.enableNotificationOnSuccess),
					notificationEmails: (selectedPiRequestInfo.notificationEmails || []).join(","),
					notificationTriggerType: deriveNotificationTriggerType(selectedPiRequestInfo),
				})
			}
		}

	}, [type, showModal, activeRecordsMetadata.managedNodeInfo]);

	const getAllWorkflows = useCallback(() => {
		DataSourceHandler.GetAllWorkflows(`${Env?.REACT_APP_PLATFORM_URL}/platform/api`, (res) => {
			setWorkflows(res.data.map((item)=>{
				return ({
					label:item.projectName,
					value:item.id,
				})
			}));
		});
	}, [Env?.REACT_APP_PLATFORM_URL]);

	useEffect(() => {
		if(Env?.REACT_APP_PLATFORM_URL){
			getAllWorkflows();
			DataSourceHandler.GetAllDags(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, (res) => {
				const dags = Array.isArray(res) ? res : res.data?.content || res.data || [];
				setDagOptions(dags.map((dag: any) => ({
					label: dag.dag_id,
					value: dag.dag_id,
				})));
			});
			DataSourceHandler.GetSinkNodes(`${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, (res) => {
				setSinkOptions(res.map((_res: any) => {
					return {
						label: _res.sinkName,
						value: _res.sinkName,
						type:  _res.sinkKind
					}
				}))
			});
		}
    },  [Env?.REACT_APP_PLATFORM_URL]);

	return (
		<Modal
			isOpen={showModal}
			toggleClose={toggleClose}
			title={<Flex justifyContent={"space-between"} width={isFullScreen?"97vw":"77vw"}>
				<div>{_modalTitles[type]} Request</div>
				<div>
					<button
						className="graphCardIcon"
						onClick={() =>setIsFullScreen(!isFullScreen)}
					>
						{isFullScreen ? (<RestoreIcon />) : (<GoToFullScreenIcon />)}
					</button>
				</div>
			</Flex>}
			subtitle={`${
				type !== "new"
					? `${selectedPiRequestInfo?.requestName ??
							selectedPiRequestInfo?.id} - `
					: ""
			}${activeRecordsMetadata.connectionName}`}
			className={`queryBuilderModal__container newPiRequestModal__container ${isFullScreen ? "fullscreen" : "normal"}`}
		>
			<Form
				initialValues={initialValues}
				onSubmit={handleOnSubmit}
				validationSchema={activeRecordsMetadata.sourceType=='OPCUA'?OpcuaFormSchema:formSchema}
				enableReinitialize
				className={styles.piRequestForm}
			>
				<div className={styles.piRequestFormContent}>
				{type !== "view" && (
					<div className={styles.requestConfigurationScroll}>
						<div className={styles.requestBasicsGrid}>
							<InputField
								name="requestName"
								label="Request Name"
								required
								className={styles["requestName__field"]}
							/>
							<NewSelectField
								name="sinkName"
								label="Choose Sink"
								options={sinkOptions}
								required={true}
								className={styles["requestName__field"]}
							/>
						</div>
						<div className={styles.triggerConfigurationGrid}>
							<TriggerConfigurationFields
								trigger="success"
								workflowOptions={workflowOptions}
								dagOptions={dagOptions}
							/>
							<TriggerConfigurationFields
								trigger="failure"
								workflowOptions={workflowOptions}
								dagOptions={dagOptions}
							/>
						</div>
						<NotificationConfigurationFields
							userOptions={notificationUserOptions}
							currentUserEmail={activeUserInfo?.email}
						/>
					</div>
				)}

				<CustomMonacoEditor
					{...editorControllerProps}
					editorHeight={isFullScreen?(type === "view"?"690":"500"):(type === "view"?580:450)}
					editorWidth={isFullScreen?"100vw":'100%'}
					language="json"
					readOnly={type === "view"}
					toolbarOptions={
						type === "view" ? ["Copy"] : ["Copy", "Clear"]
					}
					label="Request Body"
					required
				/>
				</div>
				<div className="actionButtons__box">
					{type === "edit" || type === "new" ? (
						<>
							<button
								className="btn-md c-w"
								onClick={toggleClose}
								type="button"
							>
								Cancel
							</button>
							<button className="btn-md btn-yellow" type="submit">
								{type === "edit" ? "Update" : "Create"}
							</button>
						</>
					) : (
						<button
							className="btn-md btn-grey"
							onClick={toggleClose}
							type="button"
						>
							Close
						</button>
					)}
				</div>
			</Form>
		</Modal>
	);
};

export { NewEditPiRequestModal };
